import {
  ChatMessage,
  ChatThread,
  ModeListResponse,
  SearchResult,
  SwitchModeRequest,
  SwitchModeResponse,
  ThreadModeResponse,
  WebSearchStatus,
} from '../types/chat';
import { v4 as uuidv4 } from 'uuid';

// Use REACT_APP_CHAT_API_URL for chat endpoints to avoid URL duplication
// REACT_APP_API_URL includes /api/v1 suffix, which would cause double path issues
const API_BASE_URL = process.env.REACT_APP_CHAT_API_URL || 'http://localhost:8000';

const sanitizeSearchSnippet = (value?: string): string => {
  return String(value || '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/<[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

interface FileSearchResponse {
  query: string;
  results: Array<{
    id: string;
    title: string;
    snippet: string;
    file_name: string;
    relevance_score: number;
    page_number?: number;
    pageNumber?: number;
    segment_id?: string;
    segmentId?: string;
  }>;
  total: number;
}

class ChatService {
  /**
   * Get access token from sessionStorage
   */
  private getAccessToken(): string | null {
    return sessionStorage.getItem('access_token');
  }

  /**
   * Get headers with Authorization token
   * Cookie認証廃止により、Authorization headerのみを使用
   */
  private getHeaders(includeContentType: boolean = true): HeadersInit {
    const headers: HeadersInit = {};

    if (includeContentType) {
      headers['Content-Type'] = 'application/json';
    }

    const token = this.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  async createThread(title?: string): Promise<ChatThread> {
    const response = await fetch(`${API_BASE_URL}/api/v1/chat/threads`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        title: title || 'New Chat',
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create thread');
    }

    const data = await response.json();

    return {
      id: data.id,
      title: data.title,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at),
      isPinned: Boolean(data.is_pinned),
      pinnedAt: data.pinned_at ? new Date(data.pinned_at) : null,
      messages: [],
    };
  }

  async getThread(threadId: string): Promise<ChatThread> {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/chat/threads/${threadId}`,
      {
        headers: this.getHeaders(false),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to get thread');
    }

    const data = await response.json();

    // Get messages for the thread
    const messages = await this.getMessages(threadId);

    return {
      id: data.id,
      title: data.title,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at),
      isPinned: Boolean(data.is_pinned),
      pinnedAt: data.pinned_at ? new Date(data.pinned_at) : null,
      messages,
    };
  }

  async getMessages(threadId: string): Promise<ChatMessage[]> {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/chat/threads/${threadId}/messages`,
      {
        headers: this.getHeaders(false),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to get messages');
    }

    const data = await response.json();

    return data.messages.map((msg: any) => {
      // Map sources to SearchResult format for SearchResultsCard
      const sources = msg.sources || [];
      const mappedSources = sources.map((src: any) => ({
        id: src.fileId || src.documentId || src.id || '',
        title: src.title || 'Unknown',
        path: ['General', 'Files'],
        type: 'document' as const,
        snippet: sanitizeSearchSnippet(src.snippet || src.excerpt || ''),
        excerpt: sanitizeSearchSnippet(src.excerpt || src.snippet || ''),
        fileName: src.fileName || src.file_name || src.title || 'Unknown',
        relevance: src.relevance_score || src.relevanceScore || 0.5,
        relevanceScore: src.relevanceScore || src.relevance_score || 0.5,
        rawRelevanceScore: src.rawRelevanceScore ?? src.raw_relevance_score,
        relevanceLabel: src.relevanceLabel ?? src.relevance_label,
        relevanceSource: src.relevanceSource ?? src.relevance_source,
        citationIndex: src.citationIndex ?? src.citation_index,
        selected: src.selected,
        pageNumber: src.pageNumber ?? src.page_number,
        segmentId: src.segmentId ?? src.segment_id,
      }));

      return {
        id: msg.id,
        role: msg.role as 'user' | 'assistant',
        content: msg.content,
        timestamp: new Date(msg.created_at),
        sources: sources,
        // Build searchResults if sources exist (for SearchResultsCard display)
        searchResults: mappedSources.length > 0 ? {
          query: msg.metadata?.search_query || '',
          results: mappedSources,
        } : undefined,
        // Hydrate verification + grounding from persisted metadata
        groundingStatus: msg.metadata?.grounding_status,
        verificationSummary: msg.metadata?.verification_summary,
        webSearchStatus: msg.metadata?.web_search_status,
      };
    });
  }

  async searchFiles(threadId: string, query: string): Promise<SearchResult[]> {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/chat/threads/${threadId}/file-search?` +
      new URLSearchParams({
        q: query,
        limit: '5',
      }),
      {
        headers: this.getHeaders(false),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to search files');
    }

    const data: FileSearchResponse = await response.json();

    return data.results.map(result => ({
      id: result.id,
      title: result.title,
      snippet: sanitizeSearchSnippet(result.snippet),
      fileName: result.file_name,
      relevanceScore: result.relevance_score,
      pageNumber: result.pageNumber ?? result.page_number,
      segmentId: result.segmentId ?? result.segment_id,
    }));
  }

  async streamMessage(
    threadId: string,
    message: string,
    onMessage: (content: string) => void,
    onSearchResults?: (results: SearchResult[]) => void,
    onComplete?: (modeInfo?: {
      mode?: string;
      modeSwitched?: boolean;
      newMode?: string;
      groundingStatus?: string;
      verificationSummary?: any;
      webSearchStatus?: WebSearchStatus;
    }) => void,
    options?: {
      mode?: string;
      modeData?: Record<string, any>;
      preferredLanguage?: string;
      selectedFiles?: string[];
      includeWebSearch?: boolean;
      onTitleUpdate?: (title: string) => void;
    }
  ): Promise<void> {
    const params: Record<string, string> = { message };
    if (options?.mode) {
      params.mode = options.mode;
    }
    if (options?.modeData) {
      params.mode_data = JSON.stringify(options.modeData);
    }
    if (options?.preferredLanguage) {
      params.preferred_language = options.preferredLanguage;
    }
    if (options?.selectedFiles && options.selectedFiles.length > 0) {
      params.selected_files = options.selectedFiles.join(',');
    }
    if (options?.includeWebSearch) {
      params.include_web = 'true';
    }

    const url = `${API_BASE_URL}/api/v1/chat/threads/${threadId}/stream?` +
      new URLSearchParams(params);

    const response = await fetch(url, {
      headers: this.getHeaders(false),
    });

    if (!response.ok) {
      throw new Error('Failed to stream message');
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let fullMessage = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data) {
              try {
                const event = JSON.parse(data);

                if (event.type === 'content') {
                  fullMessage += event.data;
                  onMessage(fullMessage);
                } else if (event.type === 'search_results' && onSearchResults) {
                  const results: SearchResult[] = event.results.map((r: any) => ({
                    id: r.id,
                    title: r.title,
                    snippet: sanitizeSearchSnippet(r.snippet),
                    excerpt: sanitizeSearchSnippet(r.excerpt || r.snippet || ''),
                    fileName: r.file_name,
                    relevanceScore: r.relevance_score,
                    rawRelevanceScore: r.rawRelevanceScore ?? r.raw_relevance_score,
                    relevanceLabel: r.relevanceLabel ?? r.relevance_label,
                    relevanceSource: r.relevanceSource ?? r.relevance_source,
                    pageNumber: r.pageNumber ?? r.page_number,
                    segmentId: r.segmentId ?? r.segment_id,
                  }));
                  onSearchResults(results);
                } else if (event.type === 'sources' && onSearchResults) {
                  // Handle sources as search results
                  // Note: Backend sends fileId (from PR #167), but also check documentId and id for compatibility
                  const results: SearchResult[] = event.sources.map((s: any) => ({
                    id: s.fileId || s.documentId || s.id,
                    title: s.title || 'Unknown Document',
                    snippet: sanitizeSearchSnippet(s.snippet || s.excerpt || ''),
                    excerpt: sanitizeSearchSnippet(s.excerpt || s.snippet || ''),
                    fileName: s.fileName || s.title || 'Unknown',
                    relevanceScore: s.relevanceScore || 0.5,
                    rawRelevanceScore: s.rawRelevanceScore,
                    relevanceLabel: s.relevanceLabel,
                    relevanceSource: s.relevanceSource,
                    citationIndex: s.citationIndex,
                    selected: s.selected,
                    pageNumber: s.pageNumber ?? s.page_number,
                    segmentId: s.segmentId ?? s.segment_id,
                  }));
                  onSearchResults(results);
                } else if (event.type === 'title_update' && options?.onTitleUpdate) {
                  options.onTitleUpdate(event.title);
                } else if (event.type === 'completed' && onComplete) {
                  onComplete({
                    mode: event.mode,
                    modeSwitched: event.mode_switched,
                    newMode: event.new_mode,
                    groundingStatus: event.grounding_status,
                    verificationSummary: event.verification_summary,
                    webSearchStatus: event.web_search_status,
                  });
                }
              } catch (e) {
                console.error('Failed to parse SSE event:', e);
              }
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  async sendMessage(threadId: string, message: string): Promise<ChatMessage> {
    // Use streaming API and collect the response
    let fullResponse = '';
    let searchResults: SearchResult[] = [];

    await this.streamMessage(
      threadId,
      message,
      (content) => {
        fullResponse = content;
      },
      (results) => {
        searchResults = results;
      }
    );

    // Return a ChatMessage object
    // TODO: The streaming API should return the message ID from the server
    // For now, generate a client-side UUID as a placeholder
    return {
      id: uuidv4(),
      role: 'assistant',
      content: fullResponse,
      timestamp: new Date(),
      searchResults: searchResults.length > 0 ? {
        query: message,
        results: searchResults,
      } : undefined,
    };
  }

  async getThreads(): Promise<ChatThread[]> {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/chat/threads?limit=50`,
      {
        headers: this.getHeaders(false),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to get threads');
    }

    const data = await response.json();

    return data.threads.map((thread: any) => ({
      id: thread.id,
      title: thread.title,
      createdAt: new Date(thread.created_at),
      updatedAt: new Date(thread.updated_at),
      isPinned: Boolean(thread.is_pinned),
      pinnedAt: thread.pinned_at ? new Date(thread.pinned_at) : null,
      messages: [],
    }));
  }

  async updateThreadPin(threadId: string, isPinned: boolean): Promise<ChatThread> {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/chat/threads/${threadId}/pin`,
      {
        method: 'PATCH',
        headers: this.getHeaders(),
        body: JSON.stringify({ is_pinned: isPinned }),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to update thread pin');
    }

    const data = await response.json();

    return {
      id: data.id,
      title: data.title,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at),
      isPinned: Boolean(data.is_pinned),
      pinnedAt: data.pinned_at ? new Date(data.pinned_at) : null,
      messages: [],
    };
  }

  // ============================================================
  // Mode Management API Methods (Issue #302)
  // ============================================================

  /**
   * Get all available chat modes
   */
  async getModes(): Promise<ModeListResponse> {
    const response = await fetch(`${API_BASE_URL}/api/v1/chat/modes`, {
      headers: this.getHeaders(false),
    });

    if (!response.ok) {
      throw new Error('Failed to get modes');
    }

    const data = await response.json();

    return {
      modes: data.modes.map((mode: any) => ({
        id: mode.id,
        name: mode.name,
        description: mode.description,
        color: mode.color,
        icon: mode.icon,
        isActive: mode.is_active,
        isCustom: mode.is_custom,
        sortOrder: mode.sort_order,
        settings: mode.settings,
      })),
      total: data.total,
    };
  }

  /**
   * Get current mode for a thread
   */
  async getThreadMode(threadId: string): Promise<ThreadModeResponse> {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/chat/threads/${threadId}/mode`,
      {
        headers: this.getHeaders(false),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to get thread mode');
    }

    const data = await response.json();

    return {
      thread_id: data.thread_id,
      current_mode: data.current_mode,
      mode: {
        id: data.mode.id,
        name: data.mode.name,
        description: data.mode.description,
        color: data.mode.color,
        icon: data.mode.icon,
        isActive: data.mode.is_active,
        isCustom: data.mode.is_custom,
        sortOrder: data.mode.sort_order,
        settings: data.mode.settings,
      },
    };
  }

  /**
   * Switch mode for a thread
   */
  async switchMode(
    threadId: string,
    request: SwitchModeRequest
  ): Promise<SwitchModeResponse> {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/chat/threads/${threadId}/mode`,
      {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(request),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to switch mode');
    }

    const data = await response.json();

    return {
      mode: {
        id: data.mode.id,
        name: data.mode.name,
        description: data.mode.description,
        color: data.mode.color,
        icon: data.mode.icon,
        isActive: data.mode.is_active,
        isCustom: data.mode.is_custom,
        sortOrder: data.mode.sort_order,
        settings: data.mode.settings,
      },
      message: data.message,
      previous_mode: data.previous_mode,
    };
  }
}

export const chatService = new ChatService();
export default chatService;
